/**
 * <editorial-feature>
 * Initialises a Swiper carousel for the "Editorial — Collection Image with Text" section.
 * Swiper is loaded as a global from the CDN <script> in the section; we poll briefly in
 * case this module runs before the bundle has finished loading.
 *
 * When the media sits on the LEFT on desktop, the carousel slides right-to-left (RTL).
 * On mobile it always uses the default (LTR) direction. Controlled via the
 * `data-ef-desktop-rtl` attribute; direction is switched responsively at the 1200px
 * breakpoint with Swiper's changeLanguageDirection().
 */
class EditorialFeature extends HTMLElement {
  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.dirCleanup?.();
    this.swiper?.destroy?.(true, true);
  }

  #init(attempt = 0) {
    const container = this.querySelector('.swiper');
    if (!container) return;

    if (!window.Swiper) {
      if (attempt > 100) return; // ~5s safety cap
      setTimeout(() => this.#init(attempt + 1), 50);
      return;
    }

    this.swiper = new window.Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 0,
      grabCursor: true,
      navigation: {
        prevEl: this.querySelector('.editorial-feature__nav-btn--prev'),
        nextEl: this.querySelector('.editorial-feature__nav-btn--next'),
      },
      a11y: {
        prevSlideMessage: 'Previous image',
        nextSlideMessage: 'Next image',
      },
      breakpoints: {
        1200: {
          spaceBetween: 24,
          slidesPerView: 1.06,
        },
      },
    });

    this.#setupDirection();
  }

  #setupDirection() {
    const desktopRtl = this.dataset.efDesktopRtl === 'true';
    // An RTL page (Arabic) mirrors the layout, which moves the media to the other side.
    const pageRtl = document.documentElement.dir === 'rtl';
    if ((!desktopRtl && !pageRtl) || !this.swiper) return;

    const desktopDir = desktopRtl !== pageRtl ? 'rtl' : 'ltr';
    const mobileDir = pageRtl ? 'rtl' : 'ltr';
    const mql = window.matchMedia('(min-width: 1200px)');
    const apply = () => {
      // changeLanguageDirection is a no-op when the direction is unchanged, so it's safe
      // to call on every match change.
      this.swiper.changeLanguageDirection(mql.matches ? desktopDir : mobileDir);
    };

    apply();
    mql.addEventListener('change', apply);
    this.dirCleanup = () => mql.removeEventListener('change', apply);
  }
}

if (!customElements.get('editorial-feature')) {
  customElements.define('editorial-feature', EditorialFeature);
}
